import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Camera, Send, Search, Scan } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { ProductSearchDropdown } from "./ProductSearchDropdown";
import { BarcodeScanner } from "./BarcodeScanner";
import { useCreditReasons } from "@/hooks/useCreditReasons";
import { useCreateCreditRequest, useCreditRequestsByOrder } from "@/hooks/useCreditRequests";
import { supabase } from "@/integrations/supabase/client";

interface Product {
  id: string;
  barcode: string;
  name: string;
  boxes: number;
  unitsPerBox: number;
  photos: string[];
  hasCredit: boolean;
  cost_per_unit?: number;
  suggested_retail_price?: number;
}

import { Order } from "@/hooks/useOrdersManagement/types";

interface CreditRequestFormProps {
  onBack: () => void;
  order: Order;
  products: Product[];
}

export const CreditRequestForm = ({ onBack, order, products }: CreditRequestFormProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [showBarcodeScanner, setShowBarcodeScanner] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    reasonId: "",
    description: "",
    quantity: 1,
    photos: [] as string[]
  });
  
  const { toast } = useToast();
  const { data: creditReasons } = useCreditReasons();
  const createCreditRequest = useCreateCreditRequest();
  const { data: existingCredits } = useCreditRequestsByOrder(order.id);

  // Filter products based on search query
  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    product.barcode.includes(searchQuery)
  );

  const handleProductSelect = (product: Product) => {
    setSelectedProduct(product);
    setSearchQuery(product.name);
    setShowSearchDropdown(false);
  };

  const handleBarcodeDetected = (barcode: string) => {
    const product = products.find(p => p.barcode === barcode);
    if (product) {
      setSelectedProduct(product);
      setSearchQuery(product.name);
      setShowBarcodeScanner(false);
      toast({
        title: "Product found",
        description: `${product.name} selected`
      });
    } else {
      toast({
        title: "Product not found",
        description: "No product found with this barcode",
        variant: "destructive"
      });
    }
  };

  const uploadPhoto = async (file: File): Promise<string> => {
    const fileExt = file.name.split('.').pop();
    const fileName = `${Date.now()}.${fileExt}`;
    const filePath = `credit-photos/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('credit-photos')
      .upload(filePath, file);

    if (uploadError) {
      throw uploadError;
    }

    const { data: { publicUrl } } = supabase.storage
      .from('credit-photos')
      .getPublicUrl(filePath);

    return publicUrl;
  };

  const handlePhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    setUploading(true);
    try {
      const uploadPromises = Array.from(files).map(uploadPhoto);
      const photoUrls = await Promise.all(uploadPromises);
      
      setFormData(prev => ({
        ...prev,
        photos: [...prev.photos, ...photoUrls]
      }));

      toast({
        title: "Photos uploaded",
        description: `${photoUrls.length} photo(s) added successfully`
      });
    } catch (error) {
      console.error('Error uploading photos:', error);
      toast({
        title: "Upload failed",
        description: "Failed to upload photos",
        variant: "destructive"
      });
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = (index: number) => {
    setFormData(prev => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedProduct) {
      toast({
        title: "Error",
        description: "Please select a product",
        variant: "destructive"
      });
      return;
    }

    if (!formData.reasonId) {
      toast({
        title: "Error",
        description: "Please select a reason for the credit request",
        variant: "destructive"
      });
      return;
    }

    if (formData.quantity <= 0) {
      toast({
        title: "Error",
        description: "Quantity must be greater than 0",
        variant: "destructive"
      });
      return;
    }

    const selectedReason = creditReasons?.find(r => r.id === formData.reasonId);
    if (!selectedReason) return;

    // Calculate unit price from product data
    const unitPrice = selectedProduct.cost_per_unit || selectedProduct.suggested_retail_price || 0;
    const totalAmount = unitPrice * formData.quantity;

    if (totalAmount <= 0) {
      toast({
        title: "Error", 
        description: "Cannot calculate credit amount. Product must have a valid cost or retail price.",
        variant: "destructive"
      });
      return;
    }

    try {
      await createCreditRequest.mutateAsync({
        product_id: selectedProduct.id,
        product_name: selectedProduct.name,
        order_id: order.id,
        credit_reason_id: formData.reasonId,
        quantity: formData.quantity,
        unit_price: unitPrice,
        total_amount: totalAmount,
        photos: formData.photos,
        notes: formData.description,
        reason: selectedReason.reason_name,
        invoice_item_id: null // POS credit requests don't require invoice items
      });

      // Reset form
      setSelectedProduct(null);
      setSearchQuery("");
      setFormData({
        reasonId: "",
        description: "",
        quantity: 1,
        photos: []
      });
    } catch (error) {
      console.error('Error submitting credit request:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-white">
      <div className="container mx-auto p-6">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button variant="outline" onClick={onBack} className="flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Credit Request</h1>
            <p className="text-gray-600">Report damaged or short dated items for {order.supplierName}</p>
          </div>
        </div>

        {/* Credit Request Form */}
        <Card className="max-w-2xl mx-auto mb-6">
          <CardHeader>
            <CardTitle>Create Credit Request</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Product Search */}
              <div className="space-y-2">
                <Label htmlFor="product">Select Product *</Label>
                <div className="relative">
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <Input
                        id="product"
                        type="text"
                        placeholder="Search products by name or barcode..."
                        value={searchQuery}
                        onChange={(e) => {
                          setSearchQuery(e.target.value);
                          setShowSearchDropdown(e.target.value.length > 0);
                          if (e.target.value === "") {
                            setSelectedProduct(null);
                          }
                        }}
                        className="pl-10"
                        onFocus={() => setShowSearchDropdown(searchQuery.length > 0)}
                        required
                      />
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowBarcodeScanner(true)}
                    >
                      <Scan className="h-4 w-4" />
                    </Button>
                  </div>
                  
                  {showSearchDropdown && (
                    <ProductSearchDropdown
                      products={filteredProducts}
                      onSelect={handleProductSelect}
                      onClose={() => setShowSearchDropdown(false)}
                    />
                  )}
                </div>
                {selectedProduct && (
                  <div className="text-sm text-green-600">
                    Selected: {selectedProduct.name} (Barcode: {selectedProduct.barcode})
                  </div>
                )}
              </div>

              {showBarcodeScanner && (
                <div className="mb-6">
                  <BarcodeScanner
                    onScanResult={handleBarcodeDetected}
                    onClose={() => setShowBarcodeScanner(false)}
                  />
                </div>
              )}

              {/* Reason Selection */}
              <div className="space-y-2">
                <Label htmlFor="reason">Reason *</Label>
                <Select value={formData.reasonId} onValueChange={(value) => setFormData(prev => ({ ...prev, reasonId: value }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a reason" />
                  </SelectTrigger>
                  <SelectContent>
                    {creditReasons?.map((reason) => (
                      <SelectItem key={reason.id} value={reason.id}>
                        {reason.reason_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Quantity */}
              <div className="space-y-2">
                <Label htmlFor="quantity">Quantity *</Label>
                <Input
                  id="quantity"
                  type="number"
                  min="1"
                  value={formData.quantity}
                  onChange={(e) => setFormData(prev => ({ ...prev, quantity: parseInt(e.target.value) || 1 }))}
                  required
                />
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe the damage, short date, or issue in detail..."
                  rows={4}
                  required
                />
              </div>

              {/* Photo Upload */}
              <div className="space-y-2">
                <Label>Photos</Label>
                <div className="space-y-2">
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handlePhotoUpload}
                    className="hidden"
                    id="photo-upload"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => document.getElementById('photo-upload')?.click()}
                    disabled={uploading}
                    className="w-full flex items-center gap-2"
                  >
                    <Camera className="w-4 h-4" />
                    {uploading ? 'Uploading...' : `Add Photos (${formData.photos.length} uploaded)`}
                  </Button>
                  
                  {formData.photos.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {formData.photos.map((photoUrl, index) => (
                        <div key={index} className="relative">
                          <img
                            src={photoUrl}
                            alt={`Credit photo ${index + 1}`}
                            className="w-16 h-16 object-cover rounded border"
                          />
                          <button
                            type="button"
                            onClick={() => removePhoto(index)}
                            className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 text-xs flex items-center justify-center hover:bg-red-600"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Submit Button */}
              <div className="pt-4">
                <Button 
                  type="submit" 
                  className="w-full bg-orange-600 hover:bg-orange-700"
                  disabled={createCreditRequest.isPending}
                >
                  <Send className="w-4 h-4 mr-2" />
                  {createCreditRequest.isPending ? 'Submitting...' : 'Submit Credit Request'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Existing Credit Requests */}
        {existingCredits && existingCredits.length > 0 && (
          <Card className="max-w-2xl mx-auto">
            <CardHeader>
              <CardTitle>Existing Credit Requests</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {existingCredits.map((credit) => (
                  <div key={credit.id} className="border rounded p-3">
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="font-medium">{credit.product_name}</h4>
                      <Badge variant={credit.status === 'approved' ? 'default' : 'secondary'}>
                        {credit.status}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mb-1">
                      Reason: {credit.reason}
                    </p>
                    {credit.quantity && (
                      <p className="text-sm text-muted-foreground mb-1">
                        Quantity: {credit.quantity}
                      </p>
                    )}
                    {credit.total_amount && (
                      <p className="text-sm font-medium">
                        Amount: ${credit.total_amount.toFixed(2)}
                      </p>
                    )}
                    {credit.notes && (
                      <p className="text-sm text-muted-foreground mt-2">
                        Notes: {credit.notes}
                      </p>
                    )}
                    {credit.photos && credit.photos.length > 0 && (
                      <div className="flex gap-2 mt-2">
                        {credit.photos.map((photoUrl, index) => (
                          <img
                            key={index}
                            src={photoUrl}
                            alt={`Credit photo ${index + 1}`}
                            className="w-12 h-12 object-cover rounded border"
                          />
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};
